import React, { useEffect, useState } from "react";
import {
  getCompromisos,
  putCompromiso,
} from "../../componentes/http/actions/CompromisoAction";
import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  TablePagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
} from "@mui/material";
import EditarCompromisoModal from "../../componentes/modal/Compromisos/EditarCompromisoModal";
import FilterComponent from "../../componentes/data/FilterComponent";

const GestorCompromiso = () => {
  const [compromisos, setCompromisos] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [compromisoSeleccionado, setCompromisoSeleccionado] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterHabilitado, setFilterHabilitado] = useState("todos");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetchCompromisos();
  }, []);

  const fetchCompromisos = () => {
    setIsLoading(true);
    getCompromisos(localStorage.getItem("token"))
      .then((response) => {
        setCompromisos(response);
      })
      .catch((error) => {
        console.log(error);
        setSnackbar({
          open: true,
          message: "Error al cargar los compromisos",
          severity: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        setInitialDataLoaded(true);
      });
  };
  
  const filterOptions = [
    { value: "todos", label: "Todos" },
    { value: "habilitado", label: "Habilitados" },
    { value: "deshabilitado", label: "Deshabilitados" },
  ];

  // Función para obtener compromisos filtrados (sin paginación)
  const getFilteredCompromisos = () => {
    return compromisos.filter((compromiso) => {
      const matchesSearch = compromiso.nombre
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterHabilitado === "todos" ||
        (filterHabilitado === "habilitado" && compromiso.habilitado === true) ||
        (filterHabilitado === "deshabilitado" && compromiso.habilitado === false);
      return matchesSearch && matchesFilter;
    });
  };

  // Compromisos filtrados con paginación
  const filteredCompromisos = getFilteredCompromisos().slice(
    page * rowsPerPage, 
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleOpenEditModal = (compromiso) => {
    setCompromisoSeleccionado(compromiso);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setCompromisoSeleccionado(null);
  };

  const handleEditarCompromiso = async (compromisoEditado) => {
    try {
      setIsLoading(true);
      await putCompromiso(localStorage.getItem("token"), compromisoEditado);
      await fetchCompromisos();
      setSnackbar({
        open: true,
        message: "Compromiso editado con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al editar compromiso:", error);
      setSnackbar({
        open: true,
        message: "Error al editar compromiso",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderMobileCard = (compromiso) => (
    <Card key={compromiso.id} sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          Descripción: {compromiso.descripcion}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Normativa: {compromiso.nombreNormativa}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Habilitado: {compromiso.habilitado ? "Sí" : "No"}
        </Typography>
      </CardContent>
      <CardActions>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleOpenEditModal(compromiso)}
        >
          <EditNoteIcon />
        </Button>
      </CardActions>
    </Card>
  );

  const renderNoDataMessage = () => (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="body1" color="text.secondary" align="center">
        No se encontraron compromisos con los criterios de búsqueda actuales.
      </Typography>
    </Paper>
  );

  // Si está cargando por primera vez, mostrar solo el spinner
  if (isLoading && !initialDataLoaded) {
    return (
      <Box sx={{ paddingTop: { xs: '20px', sm: '10px' } }}>
        <Typography variant="h4" sx={{ mb: 2 }}>Compromisos</Typography>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingTop: { xs: '20px', sm: '10px' } }}>
      {/* Header */}
      <Typography variant="h4" sx={{ mb: 2 }}>
        Compromisos
      </Typography>

      {/* Filtros */}
      <Box sx={{ mb: 2 }}>
        <FilterComponent
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filterValue={filterHabilitado}
          onFilterChange={setFilterHabilitado}
          filterOptions={filterOptions}
        />
      </Box>

      {/* Contenido principal */}
      {filteredCompromisos.length > 0 ? (
        <>
          {isMobile ? (
            <Box>
              {filteredCompromisos.map(renderMobileCard)}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Id</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Normativa</TableCell>
                    <TableCell>Codigo</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCompromisos.map((compromiso, index) => (
                    <TableRow key={compromiso.id || index}>
                      <TableCell>{compromiso.id}</TableCell>
                      <TableCell>{compromiso.descripcion}</TableCell>
                      <TableCell>{compromiso.nombreNormativa}</TableCell>
                      <TableCell>{compromiso.codigoNormativa}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleOpenEditModal(compromiso)}
                            startIcon={<EditNoteIcon />}
                            disabled={isLoading}
                          >
                            EDITAR
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Paginación */}
          <TablePagination
            rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50]}
            component="div"
            count={getFilteredCompromisos().length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      ) : (
        renderNoDataMessage()
      )}

      {/* Modal de edición */}
      <EditarCompromisoModal
        open={openEditModal}
        onClose={handleCloseEditModal}
        compromiso={compromisoSeleccionado}
        onSubmit={handleEditarCompromiso}
        isLoading={isLoading}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GestorCompromiso;